"use client";

import { IconHeading } from "@/components/platform/v2/IconHeading";
import { useState } from "react";
import {
  Bot,
  Check,
  GraduationCap,
  Headphones,
  UserRoundCog,
} from "lucide-react";
import { Section, SectionHeading } from "@/components/platform/v2/Section";
import { PageCta, PageHero } from "@/components/platform/v2/PageEdge";
import { Button } from "@/components/platform/v2/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/platform/v2/ui/accordion";
import { DemoRequestDialog } from "@/components/platform/v2/DemoRequestDialog";

const directions = [
  [
    Headphones,
    "Поддержка 24/7",
    "AI-помощник всегда на связи и готов ответить на вопросы. Если его ответа недостаточно, диалог продолжит живой специалист.",
  ],
  [
    GraduationCap,
    "Программы обучения",
    "Обучайтесь работе в платформе, осваивайте новые возможности и повышайте навыки всей команды.",
  ],
  [
    UserRoundCog,
    "Менеджер внедрения",
    "Если не хватает времени или ресурсов, выделенный менеджер полностью погрузится в проект и доведёт процессы до рабочего состояния.",
  ],
] as const;
const path = [
  "Погружается в проект и процессы",
  "Определяет задачи и приоритеты",
  "Настраивает платформу под работу",
  "Доводит процессы до рабочего состояния",
  "Обучает команду на реальных задачах",
  "Передаёт понятную систему управления",
];
const fits = [
  "Нет сотрудника для внедрения",
  "Непонятно, с чего начать",
  "Нужно обучить команду",
  "Много ручной работы",
  "Нельзя останавливать текущие процессы",
];
const faq = [
  [
    "Поддержка действительно доступна 24/7?",
    "Да. AI-помощник доступен круглосуточно, а если его ответа недостаточно, к вопросу подключится живой специалист.",
  ],
  [
    "AI заменяет живого специалиста?",
    "Нет. AI помогает быстро получить ответ, а команда поддержки остаётся на связи для вопросов, где нужен человек.",
  ],
  [
    "Как проходит обучение?",
    "Программы помогают освоить платформу, новые возможности и рабочие сценарии на практике.",
  ],
  [
    "Менеджер внедрения обязателен?",
    "Нет. Это отдельная услуга для компаний, которым не хватает времени или внутренних ресурсов на запуск.",
  ],
  [
    "Что делает менеджер внедрения?",
    "Погружается в проект, настраивает процессы, доводит их до рабочего состояния и одновременно обучает команду.",
  ],
  [
    "Есть ли фиксированная стоимость внедрения?",
    "Нет. Она зависит от процессов, масштаба команды и глубины участия.",
  ],
] as const;
const SupportPageComponent = () => {
  const [open, setOpen] = useState(false);
  const openChat = () =>
    window.dispatchEvent(new CustomEvent("vcentre:open-chat"));
  return (
    <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
      <PageHero
        eyebrow="Поддержка VCENTRE.ONE"
        title={
          <>
            Вы всегда на связи
            <br />
            <span className="text-primary">с поддержкой.</span>
          </>
        }
        text={
          <p>
            AI-помощник отвечает круглосуточно. Если его ответа недостаточно, к
            диалогу подключится живой специалист.
          </p>
        }
        actions={
          <>
            <Button size="lg" variant="site" onClick={openChat}>
              Задать вопрос AI
            </Button>
            <Button
              size="lg"
              variant="siteOutline"
              onClick={() => setOpen(true)}
            >
              Подключить менеджера внедрения
            </Button>
          </>
        }
        visual={
          <img
            src={"/assets/images/platform/support-mascot-hero.png"}
            alt="AI-помощник VCENTRE.ONE всегда на связи"
            width={1254}
            height={1254}
            loading="eager"
            className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
          />
        }
      />
      <Section tone="quiet">
        <SectionHeading
          eyebrow="Всегда есть решение"
          title="Поддержка, обучение и помощь с внедрением"
          text="Получайте ответы, развивайте навыки команды или передайте запуск выделенному специалисту."
        />
        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          {directions.map(([Icon, title, text]) => (
            <article
              key={title}
              className="surface flex h-full flex-col items-center text-center"
            >
              <IconHeading
                as="h3"
                icon={<Icon className="h-7 w-7 text-primary" />}
                className="display-3"
              >
                {title}
              </IconHeading>
              <p className="body-text mt-3">{text}</p>
            </article>
          ))}
        </div>
      </Section>
      <Section>
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="eyebrow">Поддержка 24/7</p>
            <h2 className="display-2 mt-4">Задайте вопрос в любой момент.</h2>
            <p className="lede mt-5">
              AI-помощник знает платформу и готов ответить на все вопросы. Если
              нужна дополнительная помощь, связь с живым специалистом всегда
              остаётся доступной.
            </p>
            <Button variant="site" className="mt-7" onClick={openChat}>
              Открыть AI-помощника
            </Button>
          </div>
          <div className="surface border-primary/30 text-center">
            <IconHeading
              as="h3"
              icon={<Bot className="mx-auto h-8 w-8 text-primary" />}
              className="display-3"
            >
              Один диалог — вся поддержка
            </IconHeading>
            <p className="body-text mt-4">
              Не нужно искать нужный отдел или начинать заново. Опишите вопрос в
              чате и продолжайте общение там же.
            </p>
          </div>
        </div>
      </Section>
      <Section tone="quiet">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="eyebrow">Программы обучения</p>
            <h2 className="display-2 mt-4">
              Осваивайте платформу и повышайте навыки.
            </h2>
            <p className="lede mt-5">
              Обучение помогает команде увереннее работать в VCENTRE.ONE,
              использовать больше возможностей и выстраивать процессы без
              постоянной внешней помощи.
            </p>
          </div>
          <div className="surface text-center">
            <IconHeading
              as="h3"
              icon={<GraduationCap className="mx-auto h-8 w-8 text-primary" />}
              className="display-3"
            >
              Учитесь на рабочих задачах
            </IconHeading>
            <p className="body-text mt-4">
              Программы подходят для старта, знакомства с новыми функциями и
              развития навыков действующей команды.
            </p>
          </div>
        </div>
      </Section>
      <Section>
        <SectionHeading
          eyebrow="Менеджер внедрения"
          title="Полностью погружается в проект"
          text="Когда не хватает времени или ресурсов, менеджер берёт внедрение на себя: настраивает систему, доводит процессы до ума и одновременно обучает команду."
        />
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {path.map((x, i) => (
            <div key={x} className="border-t border-border/60 py-5 text-center">
              <span className="text-xs font-bold text-primary">0{i + 1}</span>
              <p className="mt-2 font-bold">{x}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section>
        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <p className="eyebrow">Кому нужен менеджер</p>
            <div className="mt-6 divide-y divide-border/60 border-y border-border/60">
              {fits.map((x) => (
                <p key={x} className="flex gap-3 py-4 text-sm">
                  <Check className="h-4 w-4 text-primary" />
                  {x}
                </p>
              ))}
            </div>
          </div>
          <div className="surface">
            <p className="eyebrow">Стоимость и границы</p>
            <h2 className="display-3 mt-4">Рассчитывается под проект.</h2>
            <p className="body-text mt-4">
              Поддержка помогает пользоваться продуктом. Внедрение,
              индивидуальная разработка, сложные интеграции и большой перенос
              данных — отдельные работы.
            </p>
            <Button
              variant="site"
              className="mt-7"
              onClick={() => setOpen(true)}
            >
              Обсудить внедрение
            </Button>
          </div>
        </div>
      </Section>
      <Section tone="quiet">
        <SectionHeading eyebrow="FAQ" title="Коротко о помощи" />
        <Accordion type="single" collapsible className="mx-auto mt-8 max-w-3xl">
          {faq.map(([q, a]) => (
            <AccordionItem key={q} value={q}>
              <AccordionTrigger>{q}</AccordionTrigger>
              <AccordionContent className="body-text">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>
      <Section>
        <PageCta
          title="Начните с одного вопроса"
          text={
            <p>
              AI-помощник ответит в любое время, а живая поддержка останется
              рядом, если понадобится больше помощи.
            </p>
          }
          actions={
            <>
              <Button variant="site" onClick={openChat}>
                Задать вопрос
              </Button>
              <Button variant="siteOutline" onClick={() => setOpen(true)}>
                Менеджер внедрения
              </Button>
            </>
          }
          visual={
            <img
              src={"/assets/images/platform/support-mascot-cta.png"}
              alt="Команда VCENTRE.ONE помогает запустить проект"
              width={1254}
              height={1254}
              loading="lazy"
              className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
            />
          }
        />
      </Section>
      <DemoRequestDialog
        open={open}
        onOpenChange={setOpen}
        title="Подключить менеджера внедрения"
      />
    </div>
  );
};
export default SupportPageComponent;
